import React, { useState, useEffect } from "react";
import AuthLayout from "../components/authLayout";
import "./style.scss";
import { Form } from "antd";
import { useNavigate } from "react-router-dom";
import BrandLogo from "assets/images/svg/click-for-marry02.svg";
import Input from "components/BaseWidgets/form/input";
import Button from "components/BaseWidgets/button";
import { useLoginWithOtp } from "hooks/api/useAuth";
import Otp from "../otp";
import RadioButton from "components/BaseWidgets/radioButton";
import Select from "components/BaseWidgets/select";
import { useSelectCode } from "hooks/api/useListing";

function LoginOtp() {
  const [isOtp, setOtp] = useState(false);
  const [displayIdentifier, setDisplayIdentifier] = useState("");
  const [loginMethod, setLoginMethod] = useState("phone");
  const [countryCode, setCountryCode] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { mutate: requestOtp, isPending } = useLoginWithOtp({
    onSuccess: () => {
      setOtp(true);
    },
  });

  const { data: countrylist } = useSelectCode({ search_key: "" });

  const countryCodesOptions = countrylist?.country_codes?.map((item) => ({
    value: item.id,
    label: item.code,
    phone_length: item.phone_length,
  }));

  useEffect(() => {
    if (countryCodesOptions && !countryCode) {
      const defaultCode = countryCodesOptions.find((c) => c.label === "+91");
      if (defaultCode) setCountryCode(defaultCode);
    }
  }, [countryCodesOptions, countryCode]);

  const onFinish = (values) => {
    const identifierValue = values.identifier || "";

    if (loginMethod === "phone" && countryCode) {
      let phoneNumber = identifierValue.replace(/\D/g, "");
      const codeWithoutPlus = countryCode.label.replace("+", "");

      if (phoneNumber.startsWith(codeWithoutPlus)) {
        phoneNumber = phoneNumber.substring(codeWithoutPlus.length);
      }

      const payload = {
        identifier: phoneNumber,
        countrycode: countryCode.value,
      };
      setDisplayIdentifier(phoneNumber);

      requestOtp(payload);
    } else {
      const payload = {
        identifier: identifierValue,
      };
      setDisplayIdentifier(identifierValue);

      requestOtp(payload);
    }
  };

  const getInputPlaceholder = () => {
    switch (loginMethod) {
      case "phone":
        return "Enter phone number";
      case "email":
        return "Enter email address";
      case "aec":
        return "Enter AEC ID";
      default:
        return "";
    }
  };

  const getInputLabel = () => {
    switch (loginMethod) {
      case "phone":
        return "Phone Number";
      case "email":
        return "Email ID";
      case "aec":
        return "AEC ID";
      default:
        return "Identifier";
    }
  };

  const getValidationRules = () => {
    const baseRules = [
      {
        required: true,
        message: `Please enter your ${loginMethod === "aec"
            ? "AEC ID"
            : loginMethod === "email"
              ? "email"
              : "phone number"
          }`,
      },
    ];

    if (loginMethod === "phone") {
      baseRules.push({
        pattern: /^[0-9]+$/,
        message: "Please enter a valid phone number (numbers only)",
      });

      if (countryCode?.phone_length) {
        baseRules.push({
          validator: (_, value) => {
            if (value && value.length !== countryCode.phone_length) {
              return Promise.reject(
                new Error(
                  `Phone number should be ${countryCode.phone_length} digits`,
                ),
              );
            }
            return Promise.resolve();
          },
        });
      }
    } else if (loginMethod === "email") {
      baseRules.push({
        type: "email",
        message: "Please enter a valid email address",
      });
    } else if (loginMethod === "aec") {
      baseRules.push({
        pattern: /^[a-zA-Z0-9]*$/,
        message: "AEC ID should not contain special characters",
      });
    }

    return baseRules;
  };

  return (
    <AuthLayout childrenClass="custom-auth-layout">
      <div className="login-otp-wrapper">
        <div className="brand-logo">
          <img className="logo" src={BrandLogo} alt="Logo" />
        </div>
        {isOtp ? (
          <Otp
            setRender={setOtp}
            phoneNumber={displayIdentifier}
            countryCode={loginMethod === "phone" && countryCode}
          />
        ) : (
          <>
            <Button isBack onClick={() => navigate("/login")}></Button>
            <div className="page-heading">
              <h2 className="title font-title-xl mb-2">Log in with OTP</h2>
              <p className="subtitle font-body-xs">
                Enter your mobile number, email ID or AEC ID and an OTP will be
                sent to you.
              </p>
            </div>

            <div className="auth-form-container">
              <Form
                form={form}
                className="login-form"
                onFinish={onFinish}
                layout="vertical"
              >
                <Form.Item className="mb-4" style={{ marginBottom: 0 }}>
                  <div className="login-bundle-box">
                    <div className="login-method-selector">
                      <RadioButton
                        value={loginMethod}
                        className="radio-button-label"
                        radioGroupClass="radio-group"
                        options={[
                          { label: "Phone number", value: "phone" },
                          { label: "Email ID", value: "email" },
                          { label: "AEC ID", value: "aec" },
                        ]}
                        onChange={(e) => {
                          setLoginMethod(e.target.value);
                          form.setFieldsValue({ identifier: "" });
                        }}
                      />
                    </div>

                    <div className="login-fields-group">
                      {loginMethod === "phone" && (
                        <div className="country-code-select">
                          <Select
                            // showSearch={false}
                            value={countryCode}
                            options={countryCodesOptions}
                            inputClassName={"user-onboarding-select"}
                            onChange={(value) => {
                              const selected = countryCodesOptions.find(
                                (item) => item.value === value,
                              );
                              setCountryCode(selected);
                              form.setFieldsValue({ identifier: "" });
                            }}
                            allowClear={false}
                          />
                        </div>
                      )}

                      <div
                        className={`custom-error input-field ${loginMethod === "phone" ? "phone-input-padding" : ""
                          }`}
                        style={{ marginBottom: 0 }}
                      >
                        <Form.Item
                          name="identifier"
                          noStyle
                          rules={getValidationRules()}
                        >
                          <Input
                            id="identifier"
                            label={getInputLabel()}
                            labelClass="label"
                            placeholder={getInputPlaceholder()}
                            type={
                              loginMethod === "email"
                                ? "email"
                                : loginMethod === "phone"
                                  ? "number"
                                  : "text"
                            }
                            maxLength={
                              loginMethod === "phone" &&
                                countryCode?.phone_length
                                ? countryCode.phone_length
                                : undefined
                            }
                            onChange={(e) => {
                              let value = e.target.value;

                              if (loginMethod === "phone") {
                                value = value.replace(/\D/g, "");
                              } else if (loginMethod === "aec") {
                                value = value.replace(/[^a-zA-Z0-9]/g, "");
                              }

                              form.setFieldsValue({ identifier: value });
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                </Form.Item>

                <Button
                  isLoading={isPending}
                  htmlType="submit"
                  onClick={() => {
                    form.validateFields().catch((err) => {

                    });
                  }}
                  className={"mt-8 mb-6"}
                  wFull
                >
                  Send OTP
                </Button>
              </Form>

              <Button
                onClick={() => navigate("/login")}
                className={"password-link"}
                variant="link"
              >
                Log in using password
              </Button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export default LoginOtp;
